import { PhotoInterface as IPhoto, PhotoInterface } from "../models/interfaces/projectFormMonsters/parts/photo.interface";
import { filter, map, take } from "rxjs/operators";

import { ActiveFormActions } from "../states/activeForm";
import { AppState } from "../states/app-state.interface";
import { DatabaseHelper } from "../services/databaseHelper.service";
import { Events } from "ionic-angular";
import { Helper } from "../services/helper.service";
import { PhotoResponse as IPhotoResponse } from "../models/responses/photo.response";
import { ProjectInterface as IProject } from "../models/interfaces/project.interface";
import { Injectable } from "@angular/core";
import { MonsterInterface } from "../models/interfaces/projectFormMonsters/Monster.interface";
import { Observable } from "rxjs/Observable";
import { Photo } from "../models/photo.model";
import { Project } from "../models/project.model";
import { ProjectProvider } from "../providers/project/project";
import { ProjectsActions } from "../states/projects";
import { Store } from "@ngrx/store";

@Injectable()
export class ProjectsRepository {
  public projects$: Observable<any> = this.s.select(res => res.projects);
  public active$: Observable<any> = this.s.select(res => res.projects.projects);
  public archive$: Observable<any> = this.s.select(res => res.projects.archive);

  private projectSyncingCounter = 0;
  private projectWaitingForSync: any;
  private inputProjectWaitingForSync = null;
  private projectSyncTimeout = null;

  constructor(
    private s: Store<AppState>,
    private projectsActions: ProjectsActions,
    private projectProvider: ProjectProvider,
    private activeFormActions: ActiveFormActions,
    private helper: Helper,
    private events: Events,
    private databaseHelper: DatabaseHelper,
  ) {
  }

  public addOrUpdateProject(project: IProject) {
    project.dirty = true;
    this.s.dispatch(this.projectsActions.addOrUpdateProject({ project }));
  }

  public archiveProject(project: IProject) {
    project.archived = true;
    project.dirty = true;

    // Remove project from active
    this.s.dispatch(this.projectsActions.removeActiveProject({ project }));

    // Save project in backend and add it to archive
    this.projectProvider.syncProject(project).subscribe(res => {
      this.s.dispatch(this.projectsActions.archiveProject({ project: Project.create(Project.formatResponse(res)) }));
    });
  }

  public unarchiveProject(project: IProject) {
    project.archived = false;
    project.dirty = true;

    // Remove project from active
    this.s.dispatch(this.projectsActions.removeArchiveProject({ project }));

    // Save project in backend and add it to archive
    this.projectProvider.syncProject(project).subscribe(res => {
      this.s.dispatch(this.projectsActions.activateProject({ project: Project.create(Project.formatResponse(res)) }));
    });
  }

  public requestLabAnalysis(project: IProject) {
    return this.projectProvider.requestLabAnalysis(project.id);
  }

  public syncProject(project: IProject) {

    // Monsters need a "monstercode" so we can find the back-end record they
    // belong to. Give all monsters without a "monstercode" a temporary one.
    project.projectFormMonsters.fields.monsters
      .filter(monster => monster.fields.general.sampleCode.length === 0)
      .forEach((monster: MonsterInterface) => {
        monster.fields.general.sampleCode = 'tijdelijk-bronnummer-' + Math.floor(Math.random() * 100000);
      });

    return this.projectProvider.syncProject(project).subscribe((responseWithoutImages: any) => {

      this.s.dispatch(this.activeFormActions.syncActiveProjectId({ id: responseWithoutImages.project.id }));

      responseWithoutImages.monsters.forEach((monster) => {
        this.s.dispatch(this.activeFormActions.syncActiveProjectMonsterId({ id: monster.id, monsterCode: monster.g_sample_code }));
      });

      // Hack: we need to make sure all the photos are saved before retrieving the project.
      this.inputProjectWaitingForSync = project;
      this.projectWaitingForSync = responseWithoutImages;
      this.projectSyncingCounter = 0;

      // If a new project that has a monster with images is saved to the back-end
      // for the first time, the images can't be saved because the monster + images
      // dont have the correct foreign key to the project yet (because the project hasn't
      // been saved to the database yet). This could be fixed neatly by refactoring the
      // entire application, but that is too much work.
      //
      // To fix this, we do the following:
      //   1. Save the project in the back-end (this ignores images)
      //   2. Map the correct project id to the monster's photos
      //   3. Save the photos in the back-end
      //   4. Retrieve the entire project from the back-end (so that it contains the photos we saved separately).
      //   5. Save the updated project to state.

      this.incrementProjectSyncingCounter();

      project.projectFormMonsters.fields.monsters.forEach((monster: MonsterInterface) => {
        // the Sample Code is not unique, but it was the only way i could think
        // of to find the back-end monster the photo belongs to.
        const backendMonster = responseWithoutImages.monsters.find(i => i.g_sample_code === monster.fields.general.sampleCode);

        // this.database.getByKey(this.databaseHelper.MONSTER_DATABASE_NAME, monster.monsterId.toString()).then(row => {
        //   let images = row ? row.images : [];
        //
        //   images.forEach(image => {
        //     image.monsterId = backendMonster.id
        //   });
        //
        //   this.database.update(this.databaseHelper.MONSTER_DATABASE_NAME, { images: images }, backendMonster.id.toString()).then(() => {
        //     this.database.delete(this.databaseHelper.MONSTER_DATABASE_NAME, monster.monsterId.toString())
        //   }).catch(e => {
        //     console.log(e.error)
        //   })
        // });
        this.syncPhotos(backendMonster.id);
      });

      this.decrementProjectSyncingCounter();
      return (responseWithoutImages.project.id)
    });
  }

  public syncPhotos(monsterId) {
    this.projectProvider.getSavedImages(monsterId).subscribe((res: { 'images': {}[] }) => {
      // this.database.getByKey(this.databaseHelper.MONSTER_DATABASE_NAME, monsterId.toString()).then(row => {
      //   let images = row ? row.images : [];
      //
      //   let newImages = [];
      //
      //   images.forEach(image => {
      //     if (!image.saved) {
      //       image.saved = true;
      //       newImages.push(image);
      //       this.projectProvider.storeV2(image).subscribe();
      //     }
      //   });
      //
      //   for (let image of res.images) {
      //     newImages.push(image);
      //   }
      //   if (!row) {
      //     this.database.add(this.databaseHelper.MONSTER_DATABASE_NAME, { images: newImages }, monsterId.toString());
      //   } else {
      //     this.database.update(this.databaseHelper.MONSTER_DATABASE_NAME, { images: newImages }, monsterId.toString());
      //   }
      // })
    });
  }

  public fetchAndCleanSync() {
    return this.projectProvider.getSavedProjects().map((res: { 'active': {}[], 'archive': {}[] }) => {
      // Format active projects
      let activeProjects: IProject[] = [];
      for (let project of res.active) {
        activeProjects.push(Project.formatResponse(project));
      }
      // Add active projects fetched from database that are not stored in local storage
      this.s.dispatch(this.projectsActions.addMissingActiveProjects({ projects: activeProjects }));
      // Format archived projects
      let archiveProjects: IProject[] = [];
      for (let project of res.archive) {
        archiveProjects.push(Project.formatResponse(project));
      }
      // Add archive projects fetched from database that are not stored in local storage
      this.s.dispatch(this.projectsActions.addMissingArchiveProjects({ projects: archiveProjects }));
    });
  }

  public storePhotos(formData: FormData) {
    return this.projectProvider.storePhotos(formData);
  }

  public destroyPhoto(id: number) {
    return this.projectProvider.destroyPhoto(id);
  }

  public forceDestroyPhoto(id: number) {
    return this.projectProvider.forceDestroyPhoto(id);
  }

  public updatePhoto(photo: IPhoto) {
    return this.projectProvider.restorePhoto(photo);
  }

  public storeV2(images) {
    return this.projectProvider.storeV2(images);
  }

  public deleteV2(file_id) {
    return this.projectProvider.deleteV2(file_id);
  }

  public validProjectNumber(projectNumber, projectId) {
    // Project numbers should be unique. This method checks if any other project
    // in the store has the same project number.
    return this.projects$.pipe(map((projects: any) => {
      const hasActiveProject = projects.projects.filter(project => {
        return project.id !== projectId && project.projectForm.fields.settings.projectNumber === projectNumber;
      }).length > 0;

      if (hasActiveProject) {
        return false;
      }

      const hasArchivedProject = projects.archive.filter(project => {
        return project.id !== projectId && project.projectForm.fields.settings.projectNumber === projectNumber;
      }).length > 0;

      if (hasArchivedProject) {
        return false;
      }

      return true;
    }));
  }

  private incrementProjectSyncingCounter() {
    this.projectSyncingCounter++;

    clearTimeout(this.projectSyncTimeout);
  }

  private decrementProjectSyncingCounter() {
    this.projectSyncingCounter--;

    if (!this.projectWaitingForSync || this.projectSyncingCounter > 0) {
      return;
    }

    this.projectSyncTimeout = setTimeout(() => {
      this.syncWaitingProject();
    }, 1000);
  }

  private syncWaitingProject() {
    this.projectProvider.getSavedProjects().subscribe((getProjectsResult: { 'active': {}[], 'archive': {}[] }) => {
      const justUpdatedProject = getProjectsResult.active.find((i: any) => i.project.id === this.projectWaitingForSync.project.id);

      const formattedJustUpdatedProject = Project.create(Project.formatResponse(justUpdatedProject));

      this.s.dispatch(this.projectsActions.syncProject({
        id: this.inputProjectWaitingForSync.id,
        saved: this.inputProjectWaitingForSync.saved,
        project: formattedJustUpdatedProject
      }));

      this.inputProjectWaitingForSync = null;

      this.projectWaitingForSync = null;

      this.events.publish('project-sync:done');
    });
  }

  public delete(projectId: number) {
    this.projectProvider.delete(projectId).subscribe((r) => {
      this.s.dispatch(this.projectsActions.delete({ projectId }));
    });
  }

  public deleteMonster(projectId: number, monsterId: number) {
    this.projectProvider.deleteMonster(projectId, monsterId).subscribe((r) => {
      this.s.dispatch(this.projectsActions.deleteMonster({ projectId, monsterId }));
      this.s.dispatch(this.activeFormActions.deleteMonster({ projectId, monsterId }));
    });
  }
}



// WEBPACK FOOTER //
// ./src/repositories/projects.repository.ts